import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import { faStar } from '@fortawesome/free-solid-svg-icons';

const MovieCard = ({ movie, onClick, releaseYear, runtime, ratingFormat = 'stars' }) => {
  if (!movie) return null;

  // Create 5‑star rating (used when ratingFormat === 'stars')
  const createStarRating = (rating) => {
    const starRating = rating / 2;
    const fullStars = Math.floor(starRating);
    const partialStar = starRating - fullStars;
    let stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <div className="star" key={`full-${i}`}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '100%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    if (partialStar > 0) {
      stars.push(
        <div className="star" key="partial">
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: `${partialStar * 100}%` }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    const emptyStars = 5 - Math.ceil(starRating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <div className="star" key={`empty-${i}`}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '0%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    return stars;
  };

  // Render rating based on format
  const renderRating = () => {
    if (ratingFormat === 'stars') {
      return (
        <div className="star-rating">
          {createStarRating(movie.vote_average || 0)}
        </div>
      );
    } else {
      const ratingNumber = movie.vote_average ? movie.vote_average.toFixed(1) : null;
      return (
        <div className="rating-container">
          <span className="rating-star"><FontAwesomeIcon icon={faStar} /></span>
          <span className="rating-number">{ratingNumber}</span>
        </div>
      );
    }
  };

  const posterPath = movie.poster_path
    ? `https://image.tmdb.org/t/p/w500${movie.poster_path}`
    : 'https://via.placeholder.com/500x750?text=No+Poster';

  // Build meta string
  const year = releaseYear || (movie.release_date ? new Date(movie.release_date).getFullYear() : null);
  const runtimeMinutes = runtime || movie.runtime || 0;
  const hours = Math.floor(runtimeMinutes / 60);
  const minutes = runtimeMinutes % 60;
  let timeStr = '';
  if (hours > 0 && minutes > 0) timeStr = `${hours}h ${minutes}m`;
  else if (hours > 0) timeStr = `${hours}h`;
  else if (minutes > 0) timeStr = `${minutes}m`;

  const metaParts = [];
  if (year) metaParts.push(year);
  if (timeStr) metaParts.push(timeStr);
  const metaText = metaParts.join(' • ');

  return (
    <div
      className="movie-card"
      data-id={movie.id}
      data-type="movie"
      onClick={() => onClick(movie)}
    >
      <div className="poster-container">
        <img src={posterPath} alt={movie.title || 'Movie'} className="movie-poster" loading="lazy" />
      </div>
      <div className="movie-details">
        <div className="movie-title">{movie.title?.toUpperCase() || 'UNTITLED'}</div>
        <div className="movie-meta-row">
          <div className="rating-wrapper">
            {renderRating()}
          </div>
          {metaText && (
            <div className="meta-info">{metaText}</div>
          )}
        </div>
      </div>

      <style jsx>{`
        .movie-card {
          display: flex;
          flex-direction: column;
          background: #1a1a1a00;
          border-radius: 8px;
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.2s;
        }
        .movie-card:hover {
          transform: scale(1.03);
        }
        .poster-container {
          position: relative;
          overflow: hidden;
          background: #1a1a1a;
        }
        .movie-poster {
          width: 100%;
          display: block;
        }
        .movie-details {
          padding: 8px 0px !important;
          text-align: left !important;
        }
        .movie-title {
          font-weight: 600;
          font-size: clamp(12px, 1.1vw, 16px);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #fff;
          letter-spacing: 0.3px;
          padding: 0 2px;
        }
        .movie-meta-row {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          width: 100%;
          gap: 6px;
          padding: 2px 2px 0;
        }
        .rating-wrapper {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          margin: 0;
          padding: 0;
        }
        .star-rating {
          display: flex;
          align-items: center;
          gap: 1px;
          margin: 0;
          padding: 0;
        }
        .star {
          position: relative;
          display: inline-block;
          font-size: clamp(10px, 0.8vw, 14px);
          color: #555;
          line-height: 1;
          margin: 0;
          padding: 0;
        }
        .star .star-filled {
          position: absolute;
          top: 0;
          left: 0;
          overflow: hidden;
          color: #59f001;
        }
        .star .far.fa-star {
          color: #444;
        }
        .rating-container {
          display: flex;
          align-items: center;
          gap: 2px;
          margin-left: 5px;
          flex-shrink: 0;
        }
        .rating-star {
          font-size: clamp(10px, 0.77vw, 13.2px);
          color: #59f001;
          line-height: 1;
        }
        .rating-star svg {
          display: inline-block;
        }
        .rating-number {
          font-size: clamp(10px, 0.77vw, 13.2px);
          color: #59f001;
          font-weight: 600;
        }
        .meta-info {
          font-size: clamp(10px, 0.77vw, 13.2px);
          color: rgba(255, 255, 255, 0.7);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex-shrink: 1;
          text-align: right;
        }
        @media (max-width: 768px) {
          .movie-details {
            padding: 4px 0px !important;
          }
          .movie-title {
            font-size: 11px;
          }
          .movie-meta-row {
            padding-left: 0 !important;
          }
          .rating-wrapper {
          scale: 1.20 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .star {
            font-size: 16px !important;
          }
          .rating-star,
          .rating-number {
            font-size: 13px !important;
          }
          .meta-info {
            font-size: 13px;
          }
        }

          @media (max-width: 480px) {
          .movie-title {
            font-size: 10px;
          }
          .star {
            font-size: 12px !important;
          }
          .rating-star,
          .rating-number {
            font-size: 12px !important;
          }
          .meta-info {
            font-size: 12px;
          }
        }
        
        @media (max-width: 430px) {
          .movie-title {
            font-size: 10px;
          }
          .star {
            font-size: 11px !important;
          }
          .rating-star,
          .rating-number {
            font-size: 11px !important;
          }
          .meta-info {
            font-size: 11px;
          }
        }
      `}</style>
    </div>
  );
};

export default MovieCard;